<script lang="ts">
const MIN_MODEL_DEPTH_FOR_DISTANCE_BOUNDS = 1e-6
</script>
<script setup lang="ts">
/*
  * - Extend OrbitControls from three
  * - Define camera behavior
*/

import type { Vec3 } from '../../stores/model-store'

import { extend, useTres } from '@tresjs/core'
import { until } from '@vueuse/core'
import {
  MOUSE,
  PerspectiveCamera,
  TOUCH,
  Vector3,
} from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
// From stage-ui-three package
import { onMounted, onUnmounted, shallowRef, toRefs, watch } from 'vue'

import { useThreeCamera } from '../../stores/camera'

/*
  * Props:
  * - model size
  * - camera target: camera looking at target
*/
const props = defineProps<{
  controlEnable: boolean
  modelSize: Vec3
  cameraTarget: Vec3
  /**
   * Also allow panning while controls are enabled. Off by default so existing
   * (web/desktop) behaviour is unchanged; touch stages opt in.
   */
  panEnabled?: boolean
}>()
/*
  * Emits:
  * - camera changed: orbit controls can receive user input and change camera's position
  * - ready
*/
const emit = defineEmits<{
  (e: 'orbitControlsCameraChanged', value: {
    newCameraPosition: Vec3
    newCameraDistance: number
  }): void
  (e: 'orbitControlsReady'): void
}>()

const {
  controlEnable,
  modelSize,
  cameraTarget,
  panEnabled,
} = toRefs(props)

extend({ OrbitControls })

const { camera: cameraTres, renderer } = useTres()
const controls = shallowRef<OrbitControls>()
const camera = shallowRef<PerspectiveCamera | null>(null)
let disposeControlsChange: (() => void) | undefined

const { cameraPosition, cameraFOV, cameraDistance } = useThreeCamera()

interface OrbitDistanceBounds {
  maxDistance: number
  minDistance: number
}



function resolveModelDistanceBounds(modelSize: Vec3): OrbitDistanceBounds | undefined {
  const modelDepth = modelSize.z

  if (!Number.isFinite(modelDepth) || modelDepth <= MIN_MODEL_DEPTH_FOR_DISTANCE_BOUNDS)
    return undefined

  return {
    maxDistance: modelDepth * 20,
    minDistance: modelDepth,
  }
}

// Initialisation on onMounted
function registerInfoFlow() {
  /*
    * Downward info flow
    * - Pinia store value updated => command take effect
  */
  // Get mode size => update min/max camera distance
  watch(modelSize, (newSize) => {
    if (!controls.value)
      return
    const distanceBounds = resolveModelDistanceBounds(newSize)
    if (!distanceBounds)
      return

    controls.value.minDistance = distanceBounds.minDistance
    controls.value.maxDistance = distanceBounds.maxDistance
    controls.value.update()
  }, { immediate: true, deep: true })
  // Get camera position => update position
  watch(cameraPosition, (newPosition) => {
    if (!camera.value || !controls.value)
      return
    camera.value.position.set(
      newPosition.x,
      newPosition.y,
      newPosition.z,
    )
    camera.value.updateProjectionMatrix()
    controls.value.update()
  }, { immediate: true, deep: true })
  // Get camera target => update target (actually the model center)
  watch(cameraTarget, (newTarget) => {
    if (!controls.value)
      return
    controls.value.target.set(newTarget.x, newTarget.y, newTarget.z)
    controls.value.update()
  }, { immediate: true, deep: true })
  // Get fov => update camera fov
  watch(cameraFOV, (newFOV) => {
    if (!camera.value || !controls.value)
      return
    camera.value.fov = newFOV
    camera.value.updateProjectionMatrix()
    controls.value.update()
  }, { immediate: true })
  // Get camera distance => update camera distance
  watch(cameraDistance, (newDistance) => {
    if (!camera.value || !controls.value)
      return
    const newPosition = new Vector3()
    const target = controls.value.target
    const direction = new Vector3().subVectors(camera.value.position, target).normalize()
    newPosition.copy(target).addScaledVector(direction, newDistance)
    camera.value.position.set(
      newPosition.x,
      newPosition.y,
      newPosition.z,
    )
    camera.value.updateProjectionMatrix()
    controls.value.update()
  })
  watch([controlEnable, panEnabled], ([newEnable]) => {
    if (!camera.value || !controls.value)
      return
    controls.value.enableRotate = newEnable
    controls.value.enableZoom = newEnable
    // `touches.TWO` maps to DOLLY_PAN and `mouseButtons.RIGHT` to PAN, so panning is
    // part of the configured gesture set — it just used to be left switched off.
    controls.value.enablePan = newEnable && panEnabled.value === true
  }, { immediate: true })

  /*
    * Upward info flow
    * - Emit info => update pinia store
  */
  // send camera update info
  const onChange = () => {
    if (!controlEnable.value || !camera.value || !controls.value)
      return

    emit(
      'orbitControlsCameraChanged',
      {
        newCameraPosition: {
          x: camera.value.position.x,
          y: camera.value.position.y,
          z: camera.value.position.z,
        },
        newCameraDistance: controls.value.getDistance(),
      },
    )
  }

  disposeControlsChange?.()
  controls.value?.addEventListener('change', onChange)
  disposeControlsChange = () => controls.value?.removeEventListener('change', onChange)
}

onMounted(async () => {
  // wait until camera is not undefined
  await until(() => cameraTres.value && renderer.domElement).toBeTruthy()
  if (!cameraTres.value || !renderer.domElement) {
    console.warn('Camera or Renderer initialisation failure!')
    return
  }
  // Narrow down the camera's type
  if (!(cameraTres.value instanceof PerspectiveCamera)) {
    console.warn('Camera is not perspective camera, type error!')
    return
  }
  camera.value = cameraTres.value as PerspectiveCamera
  // Obtain orbitControl instance
  controls.value = new OrbitControls(camera.value, renderer.domElement)
  controls.value.enablePan = false
  controls.value.enableZoom = false
  controls.value.enableRotate = false
  // Align to tresjs conventions
  controls.value.mouseButtons = {
    LEFT: MOUSE.ROTATE,
    MIDDLE: MOUSE.DOLLY,
    RIGHT: MOUSE.PAN,
  }
  controls.value.touches = {
    ONE: TOUCH.ROTATE,
    TWO: TOUCH.DOLLY_PAN,
  }

  // define watch props and emit
  registerInfoFlow()
  controls.value.update()

  emit('orbitControlsReady')
})

onUnmounted(() => {
  disposeControlsChange?.()
  disposeControlsChange = undefined
  controls.value?.dispose()
  controls.value = undefined
  camera.value = null
})

defineExpose({
  controls,
  getDistance: () => controls.value?.getDistance(),
  update: () => controls.value?.update(),
  setTarget: (target: { x: number, y: number, z: number }) => {
    if (controls.value) {
      controls.value.target.set(target.x, target.y, target.z)
      controls.value.update()
    }
  },
})
</script>

<template>
  <slot />
</template>
